import { Box, Chip, useTheme } from '@mui/material';
import { CellContext } from '@tanstack/table-core';
import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { Typography } from '../../../components/Typography/Typography';
import { ROUTES } from '../../../constants/routes';
import { TimesheetStatus, Timesheet } from '../../../types';
import { formatWeekName, getWeekStatus } from '../../../utils/formatters';
import { getWeeklyTimesheetWeekStart } from '../../../utils/getWeeklyTimesheetWeekStart';

type Props = {
  info: CellContext<Timesheet, string>;
};
export function WeekCell({ info }: Props) {
  const { colors } = useTheme();
  const { pathname } = useLocation();
  // Border styling follows the VIEW (route), not the viewer's roles: in the Submitter's own list the
  // left border is neutral; the status-coloured border is reserved for the Proxy "All Timesheets"
  // view. Keying on isSubmitter wrongly neutralised the border for a Proxy + Submitter.
  const isProxyView = pathname.includes(ROUTES.PROXY_ALL_TIMESHEETS);

  const weekStart = useMemo(() => {
    return getWeeklyTimesheetWeekStart(info.row.original);
  }, [info.row.original]);

  const weekLength = info.row.original.weekDates.length + info.row.original.holidays.length;

  const statusValue = getWeekStatus(info.row.original.weekStart);

  const isNewWeek = statusValue === 'New';

  const statusColor = isNewWeek ? colors.textStatus.active : colors.textStatus.warning;

  const notIssuedStatus = info.row.original.status !== TimesheetStatus.ISSUED;

  const borderColor = useMemo(() => {
    if (!isProxyView) return colors.surfaceBackground.highlighted;
    return isNewWeek
      ? colors.textStatus.active
      : notIssuedStatus
      ? colors.surfaceBackground.highlighted
      : colors.textStatus.warning;
  }, [isNewWeek, isProxyView, notIssuedStatus, colors]);

  const cellValue = formatWeekName(weekStart, weekLength);

  return (
    <Box
      borderLeft={'4px solid'}
      borderColor={borderColor}
      maxHeight={48}
      minHeight={48}
      display={'flex'}
      alignItems={'center'}
      paddingLeft={'16px'}
      gap={'8px'}
    >
      <Typography variant='body' color={colors.text.caption}>
        {cellValue}
      </Typography>
      {!notIssuedStatus && (
        <Chip
          style={{
            backgroundColor: colors.surfaceStatus.noAction,
            minWidth: 'auto',
            height: '28px',
          }}
          label={
            <Typography variant='body' color={statusColor}>
              {statusValue}
            </Typography>
          }
        />
      )}
    </Box>
  );
}

export default WeekCell;
